import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Badge,
  Pagination,
  Spinner,
  Modal,
  Tabs,
  Tab,
  Dropdown
} from 'react-bootstrap';
import {
  FaSearch,
  FaFilter,
  FaEye,
  FaFileInvoice,
  FaCheckCircle,
  FaTimesCircle,
  FaDownload,
  FaSignOutAlt, 
  FaUser, 
  FaBell, 
  FaQuestionCircle,
  FaTimes
} from 'react-icons/fa';
import { wfbackofficeApi, notificationApi, searchApi } from '../../services/api';
import WFBackofficeSidebar from '../../components/WFBackofficeSidebar';
import { useAuth } from '../../hooks/useAuth';
import SearchResults from '../../components/SearchResults';
import './Dashboard.css';

const WFBackofficeInvoices = () => {
  const navigate = useNavigate();
  const { logout, getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();

  // --- Notification State ---
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  // --- End Notification State ---
  
  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);
  // --- End Search State ---
  
  // --- Invoices State ---
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceCases, setSelectedInvoiceCases] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [notes, setNotes] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    limit: 10
  });
  // --- End Invoices State ---
  
  // Status mapping
  const STATUS_MAP = {
    0: { name: "Generated", variant: "info" },
    1: { name: "Processing", variant: "warning" },
    2: { name: "Disbursed", variant: "success" },
    3: { name: "Rejected", variant: "danger" }
  };

  // Payment status mapping
  const PAYMENT_STATUS_MAP = {
    0: { name: "Pending", variant: "info" },
    1: { name: "Completed", variant: "success" },
    2: { name: "Failed", variant: "danger" }
  };
  
  // Set document title and check authentication
  useEffect(() => {
    document.title = 'Wishfin Backoffice - DSO Invoices';
    
    // Check authentication
    const token = localStorage.getItem('token');
    const userJSON = localStorage.getItem('user');
    
    if (!token || !userJSON) {
      console.log('No token or user found in Invoices, redirecting to login');
      navigate('/login');
      return;
    }
    
    try {
      const user = JSON.parse(userJSON);
      if (user.user_type !== 'WFBACKOFFICE') {
        console.log('User is not WFBACKOFFICE type in Invoices, redirecting');
        navigate('/login');
        return;
      }
      
      console.log('User authenticated as WFBACKOFFICE in DSO Invoices, proceeding');
      fetchInvoices();
    } catch (error) {
      console.error('Error parsing user data in Invoices:', error);
      navigate('/login');
    }
  }, [navigate]);
  
  // Handle search input changes with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        performSearch(query);
      }, 300); // 300ms debounce
      
      setSearchTimeout(timeoutId);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };
  
  // Perform the search
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle search result click
  const handleResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
    setSearchQuery('');
  };
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchRef]);

  // --- Notification useEffect Hooks ---
  useEffect(() => {
    let intervalId;
    const fetchNotificationsCount = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsCount(); // Initial fetch
    intervalId = setInterval(fetchNotificationsCount, 60000); // Fetch every 60 seconds

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  // Fetch full notifications when dropdown is opened
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          // Fetch latest 10 notifications
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications for dropdown:', error);
          setNotifications([]); // Clear notifications on error
        } finally {
          setLoadingNotifications(false);
        }
      }
    };

    fetchFullNotifications();
  }, [showNotifications]);
  // --- End Notification useEffect Hooks ---
      
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // --- Notification Helper Functions ---
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      // Update local state optimistically
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return; // No unread notifications

    try {
      await notificationApi.markAllAsRead();
      // Update local state optimistically
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0); // Reset count
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) {
      console.error("Error formatting timestamp:", e);
      return timestamp;
    }
  };
  // --- End Notification Helper Functions ---
  
  // --- Invoices Functions ---
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Call the API to fetch DSO invoices
      try {
        const response = await wfbackofficeApi.getInvoices({
          skip: (pagination.page - 1) * pagination.limit,
          limit: pagination.limit,
          search: searchTerm,
          status: statusFilter !== 'All' ? statusFilter : undefined,
          dso_only: true // This parameter will filter only DSO invoices
        });
        
        if (response && response.data) {
          setInvoices(response.data.items || []);
          setPagination(prev => ({
            ...prev,
            total: response.data.total || 0
          }));
        }
      } catch (apiError) {
        console.error('API error when fetching DSO invoices:', apiError);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  // View invoice details
  const handleViewInvoice = async (invoiceId) => {
    setSelectedInvoice({id: invoiceId}); // Just set the ID initially
    setSelectedInvoiceCases([]);
    setShowInvoiceModal(true);
    
    try {
      // Fetch invoice details from the API
      try {
        const response = await wfbackofficeApi.getInvoiceDetails(invoiceId);
        if (response && response.data) {
          // Normalize the invoice status structure to ensure it has a consistent format
          const invoiceData = response.data;
          
          // Ensure the invoice has a consistent status structure
          if (!invoiceData.status || typeof invoiceData.status !== 'object') {
            // Create a status object if it doesn't exist
            invoiceData.status = {
              code: invoiceData.invoice_status !== undefined ? invoiceData.invoice_status : 0,
              name: STATUS_MAP[invoiceData.invoice_status !== undefined ? invoiceData.invoice_status : 0]?.name || "Unknown"
            };
          }
          
          setSelectedInvoice(invoiceData);
          
          // Fetch case details if available
          try {
            const casesResponse = await wfbackofficeApi.getInvoiceCases(invoiceId);
            console.log("WFBackoffice - Invoice Cases API Response:", casesResponse); // <-- ADDED THIS LINE

            if (casesResponse && casesResponse.data && casesResponse.data.items) {
              setSelectedInvoiceCases(casesResponse.data.items);
              console.log("WFBackoffice - Setting selectedInvoiceCases:", casesResponse.data.items); // <-- ADDED THIS LINE
            } else {
               setSelectedInvoiceCases([]); // Ensure reset if no items
               console.log("WFBackoffice - No items found in cases response or invalid structure, resetting selectedInvoiceCases"); // <-- ADDED THIS LINE
            }
          } catch (casesError) {
            console.error('Error fetching invoice cases:', casesError);
            setSelectedInvoiceCases([]);
          }
        }
      } catch (apiError) {
        console.error('API error fetching invoice details:', apiError);
        // Use mock data as fallback
        setSelectedInvoice({
          id: invoiceId,
          invoice_no: `INV-2025042${invoiceId}-00${invoiceId}`,
          dso_name: 'ABC Finance',
          dso_id: 123,
          created_at: '2025-04-25',
          amount: 125000,
          status: { code: 0, name: 'Generated' },
        });
        
        setSelectedInvoiceCases([
          { id: 101, customer_name: 'John Doe', product_name: 'Personal Loan', amount: 50000 },
          { id: 102, customer_name: 'Jane Smith', product_name: 'Home Loan', amount: 75000 },
        ]);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  // Handle invoice status change
  const handleStatusChange = async (invoiceId, newStatus) => {
    setProcessingAction(true);
    
    try {
      await wfbackofficeApi.updateInvoiceStatus(invoiceId, {
        status: parseInt(newStatus),
        notes: notes
      });
      
      // Update the invoices list
      setInvoices(invoices.map(inv => 
        inv.id === invoiceId 
          ? {
              ...inv,
              status: { 
                code: parseInt(newStatus),
                name: STATUS_MAP[parseInt(newStatus)].name
              }
            } 
          : inv
      ));
      
      // Close modal and refresh list
      setShowInvoiceModal(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice status:', error);
      alert('Failed to update invoice status. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle search
  const handleInvoiceSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    if (!status || typeof status !== 'object') {
      return <span className="backoffice-status-badge secondary">Unknown</span>;
    }
    
    const code = status.code !== undefined ? status.code : (status.invoice_status || 0);
    const statusInfo = STATUS_MAP[code] || { variant: "secondary", name: status.name || "Unknown" };
    return <span className={`backoffice-status-badge ${statusInfo.variant}`}>{statusInfo.name}</span>;
  };

  // Calculate total pages for pagination
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const paginationItems = [];
  
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 || 
      i === totalPages || 
      (i >= pagination.page - 1 && i <= pagination.page + 1)
    ) {
      paginationItems.push(
        <Pagination.Item 
          key={i} 
          active={i === pagination.page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    } else if (i === pagination.page - 2 || i === pagination.page + 2) {
      paginationItems.push(<Pagination.Ellipsis key={`ellipsis-${i}`} />);
    }
  }
  // --- End Invoices Functions ---

  return (
    <div className="backoffice-dashboard-layout">
      <WFBackofficeSidebar />
      
      <div className="backoffice-content-wrapper">
        <header className="backoffice-header">
          <div className="backoffice-greeting">
            <span className="backoffice-greeting-text">Hi, {currentUser?.name || 'Wishfin'} <span className="backoffice-greeting-emoji">👋</span></span>
          </div>
          <div className="backoffice-header-search" ref={searchRef}>
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search invoices, DSOs..." 
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={() => {
                  if (searchQuery.trim().length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
              {searchQuery && !isSearching && (
                <FaTimes 
                  className="search-clear-icon" 
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                    setSearchResults([]);
                  }} 
                />
              )}
            </div>
            
            {/* Desktop Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-container">
                <SearchResults results={searchResults} onResultClick={handleResultClick} />
              </div>
            )}
            
            {/* Mobile Search Button */}
            <div className="mobile-search-button" onClick={() => setShowSearchModal(true)}>
              <FaSearch />
            </div>
          </div>
          
          <div className="backoffice-header-actions">
            <div className="backoffice-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            {/* Notification Dropdown */}
            <Dropdown
              className="notification-dropdown backoffice-notifications"
              show={showNotifications}
              onToggle={(isOpen) => setShowNotifications(isOpen)}
              align="end"
            >
              <Dropdown.Toggle variant="link" id="dropdown-notifications" className="p-0 border-0 backoffice-header-icon">
                <FaBell size={18} />
                {unreadCount > 0 && (
                  <Badge pill bg="danger" className="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ minWidth: '320px', maxHeight: '450px', overflowY: 'auto', padding: '0' }}>
                <Dropdown.Header className="d-flex justify-content-between align-items-center sticky-top bg-light">
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <button className="btn btn-link btn-sm p-0" onClick={handleMarkAllAsRead}>
                      Mark all as read
                    </button>
                  )}
                </Dropdown.Header>
                {loadingNotifications ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <Dropdown.Item
                      key={notification.id}
                      className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="notification-content">
                          <div className="notification-title">{notification.title || notification.type}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-timestamp">{formatTimestamp(notification.created_at)}</div>
                        </div>
                        {!notification.is_read && <div className="unread-dot"></div>}
                      </div>
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-4">No new notifications</Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </Dropdown>
            <div className="backoffice-user-profile">
              <div className="backoffice-avatar">
                <FaUser />
              </div>
              <div className="backoffice-user-info">
                <span className="backoffice-user-name">
                  {currentUser?.name || 'Wishfin User'}
                </span>
                <button className="backoffice-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="backoffice-main-content">
          {/* Invoices Management Content */}
          <div className="backoffice-dashboard-welcome">
            <h1>DSO Invoice Management</h1>
            <p>Process and manage invoices raised by Distribution Service Owners</p>
          </div>
          
          {/* Filters */}
          <div className="backoffice-dashboard-card mb-4">
            <div className="backoffice-card-body">
              <Row>
                <Col md={5} lg={4}>
                  <div className="mb-3 mb-md-0">
                    <InputGroup>
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search DSO invoice number, DSO name..."
                        value={searchTerm}
                        onChange={handleInvoiceSearch}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            fetchInvoices();
                          }
                        }}
                      />
                    </InputGroup>
                  </div>
                </Col>
                <Col md={4} lg={3}>
                  <div className="mb-3 mb-md-0">
                    <InputGroup>
                      <InputGroup.Text>
                        <FaFilter />
                      </InputGroup.Text>
                      <Form.Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                      >
                        <option value="All">All Status</option>
                        {Object.entries(STATUS_MAP).map(([key, { name }]) => (
                          <option key={key} value={key}>{name}</option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </div>
                </Col>
                <Col md={3} lg={5} className="d-flex justify-content-end">
                  <Button 
                    variant="primary"
                    onClick={fetchInvoices}
                  >
                    Apply Filters
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
          
          {/* Invoices Table */}
          <div className="backoffice-dashboard-card">
            <div className="backoffice-card-header">
              <h3>DSO Invoices</h3>
            </div>
            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center p-5">
                <p>No DSO invoices found matching your criteria</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="backoffice-table">
                    <thead>
                      <tr>
                        <th>Invoice No.</th>
                        <th>DSO</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(invoice => (
                        <tr key={invoice.id}>
                          <td>{invoice.invoice_no || invoice.invoice_number}</td>
                          <td>{invoice.dso_name}</td>
                          <td>₹{invoice.amount ? invoice.amount.toLocaleString() : '0'}</td>
                          <td>{invoice.status ? renderStatusBadge(invoice.status) : <span className="backoffice-status-badge secondary">Unknown</span>}</td>
                          <td>{invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewInvoice(invoice.id)}
                            >
                              <FaEye className="me-1" /> View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center p-3 border-top">
                    <Pagination size="sm">
                      <Pagination.First 
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.page === 1}
                      />
                      <Pagination.Prev 
                        onClick={() => handlePageChange(pagination.page > 1 ? pagination.page - 1 : 1)}
                        disabled={pagination.page === 1}
                      />
                      {paginationItems}
                      <Pagination.Next 
                        onClick={() => handlePageChange(pagination.page < totalPages ? pagination.page + 1 : totalPages)}
                        disabled={pagination.page === totalPages}
                      />
                      <Pagination.Last 
                        onClick={() => handlePageChange(totalPages)}
                        disabled={pagination.page === totalPages}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Invoice Detail Modal */}
      <Modal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            DSO Invoice Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!selectedInvoice ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading invoice details...</p>
            </div>
          ) : (
            <Tabs defaultActiveKey="details" className="mb-4">
              <Tab eventKey="details" title="Invoice Details">
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Invoice Number</label>
                      <div className="fw-medium">{selectedInvoice.invoice_no}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">DSO</label>
                      <div className="fw-medium">{selectedInvoice.dso_name}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Amount</label>
                      <div className="fw-medium">₹{selectedInvoice.amount?.toLocaleString()}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Status</label>
                      <div>{renderStatusBadge(selectedInvoice.status)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Created Date</label>
                      <div className="fw-medium">{new Date(selectedInvoice.created_at).toLocaleDateString()}</div>
                    </div>
                  </Col>
                  <Col md={12} className="mt-3">
                    <Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add notes about this invoice"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>
              
              <Tab eventKey="cases" title="Related Cases">
                {selectedInvoiceCases.length === 0 ? (
                  <div className="text-center p-4">
                    <p>No cases associated with this invoice</p>
                  </div>
                ) : (
                  <table className="backoffice-table">
                    <thead>
                      <tr>
                        <th>Case Ref</th> {/* Changed from ID */}
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Product</th>
                        <th>Payout Amount</th> {/* Changed from Amount */}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoiceCases.map(caseItem => (
                        <tr key={caseItem.id}>
                          {/* Use application_no if available, otherwise id */}
                          <td>{caseItem.application_no || caseItem.id}</td>
                          
                          <td>{caseItem.customer_name || 'N/A'}</td>
                          <td>{caseItem.customer_email || 'N/A'}</td>
                          <td>{caseItem.customer_mobile || 'N/A'}</td>
                          <td>{caseItem.product_name || 'N/A'}</td>
                          <td>₹{(caseItem.payout_amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex w-100 justify-content-between">
            <Button
              variant="secondary"
              onClick={() => setShowInvoiceModal(false)}
            >
              Close
            </Button>
            
            {selectedInvoice && selectedInvoice.status && 
             ((selectedInvoice.status.code === 0) || 
              (selectedInvoice.status.invoice_status === 0)) && (
              <div>
                <Button
                  variant="danger"
                  className="me-2"
                  onClick={() => handleStatusChange(selectedInvoice.id, 3)}
                  disabled={processingAction}
                >
                  {processingAction ? <Spinner animation="border" size="sm" /> : <FaTimesCircle className="me-1" />}
                  Reject
                </Button>
                <Button
                  variant="success"
                  onClick={() => handleStatusChange(selectedInvoice.id, 1)}
                  disabled={processingAction}
                >
                  {processingAction ? <Spinner animation="border" size="sm" /> : <FaCheckCircle className="me-1" />}
                  Approve
                </Button>
              </div>
            )}
            
            {selectedInvoice && selectedInvoice.status && 
             ((selectedInvoice.status.code === 1) || 
              (selectedInvoice.status.invoice_status === 1)) && (
              <Button
                variant="success"
                onClick={() => handleStatusChange(selectedInvoice.id, 2)}
                disabled={processingAction}
              >
                {processingAction ? <Spinner animation="border" size="sm" /> : <FaCheckCircle className="me-1" />}
                Mark as Disbursed
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
      
      {/* Mobile Search Modal */}
      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered
        className="search-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mobile-search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search invoices, DSOs..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
            {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
            {searchQuery && !isSearching && (
              <FaTimes
                className="search-clear-icon"
                onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                  setSearchResults([]);
                }}
              />
            )}
          </div>
          
          {searchResults.length > 0 && (
            <div className="mobile-search-results">
              <SearchResults results={searchResults} onResultClick={handleResultClick} />
            </div>
          )}
          
          {searchQuery.trim().length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="no-results-message">
              No results found for "{searchQuery}"
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WFBackofficeInvoices;
